import { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

const POLL_INTERVAL = 30 * 60 * 1000;
const BUILD_HASH = typeof __APP_BUILD_HASH__ !== 'undefined' ? __APP_BUILD_HASH__ : null;

const RELOADED_FOR_KEY = 'version_reloaded_for';

async function hardReload(forceReloadToken, serverHash) {
  if (forceReloadToken) {
    sessionStorage.setItem('force_reload_dismissed', String(forceReloadToken));
  }
  if (serverHash) {
    sessionStorage.setItem(RELOADED_FOR_KEY, serverHash);
  }
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now());
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => {})));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (_) {}
  window.location.replace(url.toString());
}

export default function VersionChecker() {
  const [outdated, setOutdated] = useState(false);
  const timerRef = useRef(null);
  const forceReloadTokenRef = useRef(null);
  const serverHashRef = useRef(null);

  const check = useCallback(async () => {
    if (!BUILD_HASH || BUILD_HASH === 'dev') return;
    try {
      const res = await fetch('/version.json?_=' + Date.now(), { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.hash && data.hash !== BUILD_HASH) {
        serverHashRef.current = data.hash;
        // Уже перезагружались ради этого хеша, а бандл тот же — сервер отдаёт version.json
        // не от текущей сборки; повторная модалка лишь заблокирует работу.
        if (sessionStorage.getItem(RELOADED_FOR_KEY) === data.hash) {
          console.warn(`[VersionChecker] version.json=${data.hash}, бандл=${BUILD_HASH}: рассинхрон сборки`);
        } else {
          setOutdated(true);
        }
      }

      if (data.forceReload) {
        forceReloadTokenRef.current = String(data.forceReload);
        const dismissed = sessionStorage.getItem('force_reload_dismissed');
        if (dismissed !== forceReloadTokenRef.current) {
          setOutdated(true);
        }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const delay = setTimeout(check, 5_000);
    timerRef.current = setInterval(check, POLL_INTERVAL);
    return () => {
      clearTimeout(delay);
      clearInterval(timerRef.current);
    };
  }, [check]);

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [check]);

  if (!outdated) return null;

  return (
    <Dialog open maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle>Доступно обновление</DialogTitle>
      <DialogContent>
        <Typography>
          Вышла новая версия приложения. Для корректной работы необходимо обновить страницу.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => hardReload(forceReloadTokenRef.current, serverHashRef.current)} variant="contained">
          Обновить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
